import React from 'react';
import { Link } from 'react-router-dom';

interface BrandLogoProps {
  size?: 'sm' | 'md' | 'lg';
  showSubtitle?: boolean;
  linkTo?: string;
  theme?: 'light' | 'dark' | 'glass';
  className?: string;
  onClick?: () => void;
}

export const BrandLogo: React.FC<BrandLogoProps> = ({
  size = 'md',
  showSubtitle = true,
  linkTo = '/dashboard',
  theme = 'glass',
  className = '',
  onClick
}) => {
  const headerText = localStorage.getItem('app_header_text') || 'Gestão Fácil';
  const savedSubtitle = localStorage.getItem('app_header_subtitle');
  const headerSubtitle = (savedSubtitle && !['Engmont ERP', 'ENGMONT ERP', 'Painel Web'].includes(savedSubtitle))
    ? savedSubtitle
    : 'By Alonedev.com';
  const logoUrl = localStorage.getItem('report_logo_url');

  const sizeClasses = {
    sm: {
      icon: 'w-7 h-7',
      title: 'text-sm',
      sub: 'text-[9px]',
      gap: 'gap-2',
      badge: 'text-[9px] px-1 py-0.2'
    },
    md: {
      icon: 'w-9 h-9',
      title: 'text-base lg:text-lg',
      sub: 'text-[10px]',
      gap: 'gap-2.5',
      badge: 'text-[10px] px-1.5 py-0.5'
    },
    lg: {
      icon: 'w-14 h-14',
      title: 'text-2xl sm:text-3xl',
      sub: 'text-xs',
      gap: 'gap-3.5',
      badge: 'text-xs px-2 py-0.5'
    }
  }[size];

  const themeStyles = {
    glass: {
      text: 'text-white',
      sub: 'text-purple-200/90 dark:text-slate-400',
      badge: 'bg-white/20 text-white border-white/30 backdrop-blur-sm'
    },
    light: {
      text: 'text-slate-900',
      sub: 'text-slate-500',
      badge: 'bg-brand-50 text-brand-700 border-brand-200'
    },
    dark: {
      text: 'text-white',
      sub: 'text-slate-400',
      badge: 'bg-slate-800 text-brand-300 border-slate-700'
    }
  }[theme];

  const content = (
    <div className={`flex items-center ${sizeClasses.gap} group cursor-pointer select-none ${className}`} onClick={onClick}>
      {/* Icon Badge */}
      <div className={`relative ${sizeClasses.icon} shrink-0 transition-transform duration-300 group-hover:scale-105 group-active:scale-95`}>
        {logoUrl ? (
          <div className="w-full h-full rounded-xl overflow-hidden bg-white shadow-md p-1 border border-white/30 flex items-center justify-center">
            <img src={logoUrl} alt="Logo" className="w-full h-full object-contain" />
          </div>
        ) : (
          <div className="w-full h-full rounded-xl bg-gradient-to-tr from-brand-600 via-purple-600 to-indigo-500 p-[1.5px] shadow-lg shadow-brand-600/30 group-hover:shadow-brand-500/50 transition-shadow">
            <div className="w-full h-full bg-slate-950/20 backdrop-blur-sm rounded-[10px] flex items-center justify-center overflow-hidden relative">
              {/* Modern Vector Emblem */}
              <svg 
                viewBox="0 0 32 32" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg" 
                className="w-3/4 h-3/4 drop-shadow-sm transform group-hover:scale-110 transition-transform duration-300"
              >
                <defs>
                  <linearGradient id="logoGradPrimary" x1="0%" y1="100%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#ffffff" />
                    <stop offset="100%" stopColor="#e0e7ff" />
                  </linearGradient>
                  <linearGradient id="logoGradAccent" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#38bdf8" />
                    <stop offset="100%" stopColor="#818cf8" />
                  </linearGradient>
                </defs>
                
                {/* Background Tech Nodes */}
                <rect x="4" y="19" width="5" height="9" rx="1.5" fill="url(#logoGradPrimary)" fillOpacity="0.75" />
                <rect x="11" y="13" width="5" height="15" rx="1.5" fill="url(#logoGradPrimary)" fillOpacity="0.9" />
                <rect x="18" y="8" width="5" height="20" rx="1.5" fill="url(#logoGradPrimary)" />
                
                {/* Dynamic Trend Vector Arrow */}
                <path 
                  d="M5 16L12 10L18 14L27 5M27 5H21M27 5V11" 
                  stroke="url(#logoGradAccent)" 
                  strokeWidth="2.5" 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                />
                
                {/* Glow Particle Peak */}
                <circle cx="27" cy="5" r="2" fill="#38bdf8" className="animate-pulse" />
              </svg>
            </div>
          </div>
        )}
      </div>

      {/* Typography / System Name */}
      <div className="flex flex-col leading-tight">
        <div className="flex items-center gap-1.5">
          <span className={`font-extrabold tracking-tight ${sizeClasses.title} ${themeStyles.text} transition-colors group-hover:text-brand-300`}>
            {headerText}
          </span>
          <span className={`font-bold uppercase tracking-wider rounded-md border ${sizeClasses.badge} ${themeStyles.badge}`}>
            ERP
          </span>
        </div>
        {showSubtitle && (
          <span className={`font-semibold tracking-wide ${sizeClasses.sub} ${themeStyles.sub} mt-0.5`}>
            {headerSubtitle}
          </span>
        )}
      </div>
    </div>
  );

  if (linkTo) {
    return (
      <Link to={linkTo} className="inline-flex items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 rounded-xl" title="Ir para Painel de Despesas">
        {content}
      </Link>
    );
  }

  return content;
};
